import Api from "../../../../core/api";
import Quotas from "../../../../core/models/quotas";
import { ds } from "../../../../core/models/dataService";
import { trackActivity } from "../../../../core/utilities/utilities";
import { CustomerType } from "common/constants";
import { isProduction } from "js/config";

// Stripe live mode
const liveProductMap = {
    // so-called 'plan'
    "pro": {
        "product": "prod_FRozwsfXiAqlX7",
        "prices": {
            // so-called 'billingTerm'
            "month": "price_1KGq2WC6TOcbsMhjPPZ88t7x",
            "year": "plan_FRp0uyxxHheQa8",
        }
    },
    "team": {
        "product": "prod_HEQlgWSQkz3qGb",
        "prices": {
            "month": "price_1KGq4RC6TOcbsMhjchFSsrwQ",
            "year": "price_1KGq4wC6TOcbsMhjSammt9bG",
        }
    },
    "team-migrated": {
        "product": "prod_HEQlgWSQkz3qGb",
        "prices": {
            "month": "plan_HEQmYB4HfYaXxc",
            "year": "plan_HEQnBa6SxskXOc",
        }
    }
};

// Stripe test mode
const testProductMap = {
    "pro": {
        "product": "prod_FDG14WN1HT6PEM",
        "prices": {
            "month": "price_1Kv6nwC6TOcbsMhjR8o3aaUb",
            "year": "plan_FDG5JgDEJwysl1",
        }
    },
    "team": {
        "product": "prod_HEQlgWSQkz3qGb",
        "prices": {
            "month": "price_1KGq4RC6TOcbsMhjchFSsrwQ",
            "year": "price_1KGq4wC6TOcbsMhjSammt9bG",
        }
    },
    "team-migrated": {
        "product": "prod_HEQlgWSQkz3qGb",
        "prices": {
            "month": "plan_HEQmYB4HfYaXxc",
            "year": "plan_HEQnBa6SxskXOc",
        }
    }
};

const productMap = isProduction ? liveProductMap : testProductMap;

export default class BillingDataService {
    static async fetchSubscription(orgId) {
        const data = await Api.subscriptions.get({
            customer_type: orgId === undefined ? CustomerType.INDIVIDUAL : CustomerType.TEAM,
            organization_id: orgId,
        });
        return data[0];
    }

    static async fetchPaymentMethod(orgId) {
        let paymentMethodsResponse = await Api.paymentMethods.get({
            customer_type: orgId === undefined ? CustomerType.INDIVIDUAL : CustomerType.TEAM,
            organization_id: orgId
        });
        if (paymentMethodsResponse.body) {
            return paymentMethodsResponse.body[0];
        }
        return null;
    }

    static async fetchQuotas(orgId) {
        return Quotas.get(orgId || "personal");
    }

    static async cancelSubscription(subscriptionId, orgId, { immediate } = {}) {
        await Api.subscriptions.delete({
            subscriptionId,
            customerType: orgId === undefined ? CustomerType.INDIVIDUAL : CustomerType.TEAM,
            orgId,
            immediate
        });

        let sub = await this.fetchSubscription(orgId);
        if (sub) {
            let { interval, product, amount, current_period_end } = sub;
            const eventProps = {
                billing_term: interval,
                current_status: product,
                effective_date: current_period_end,
                amount: amount,
                plan: product,
                current_slide_count: ds.presentations.getWorkspaceSlideCount(orgId || "personal")
            };
            trackActivity("Upgrade", "BillingIntentCancel", null, null, eventProps, { audit: true });
        }
    }

    static async removePaymentMethod(paymentMethodId) {
        await Api.paymentMethods.delete({ paymentMethodId });
    }

    /**
     * Map hardcoded (plan, billingTerm) pair to a Stripe 'Price' ID
     * @returns { month: PriceId, year: PriceId }
     */
    static getPrices(productName) {
        const entry = productMap[productName];
        if (entry) {
            return entry.prices;
        } else {
            throw new Error("unknown product");
        }
    }

    static applyCoupon(price, promo) {
        if (promo) {
            if (promo.amountOff) {
                const discountPrice = price - (promo.amountOff / 100); // cents -> dollars
                return Math.max(discountPrice, 0);
            } else {
                return price - (price / 100 * promo.percentOff);
            }
        } else {
            return price;
        }
    }
}
